import { useCallback, useEffect } from 'react';
import type { VariantOf } from 'variant';
import { fields, isType, match, variant } from 'variant';
import _memoize from 'lodash/memoize.js';
import type { ComponentGenerationResult } from '@wix/gena';
import { AsyncCallbackStatus, useAsyncCallback } from '@wix/cli-ui-kit';

const GenerationTaskResultVariant = variant({
  Success: fields<{
    hasUpdatedDependencies: boolean;
    newFiles: ComponentGenerationResult['newFiles'];
  }>(),
  Error: {},
  Loading: {},
  NotRequested: {},
});

export type GenerationTaskResult = VariantOf<
  typeof GenerationTaskResultVariant
>;

export const useGenerationTask = (
  fn: () => Promise<ComponentGenerationResult | void>,
  predicates: unknown[]
): GenerationTaskResult => {
  const generationTask = useAsyncCallback(fn);

  useEffect(() => {
    if (
      predicates.every(Boolean) &&
      isType(generationTask.status, AsyncCallbackStatus.NotRequested)
    ) {
      void generationTask.execute();
    }
  }, [generationTask, predicates]);

  // `exhaustive-deps` doesn't know how to parse function wrapped in `memoize`
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const successResult = useCallback(
    _memoize(
      (
        hasUpdatedDependencies: boolean,
        newFiles: ComponentGenerationResult['newFiles']
      ) => {
        return GenerationTaskResultVariant.Success({
          hasUpdatedDependencies,
          newFiles,
        });
      }
    ),
    []
  );

  return match(generationTask.status, {
    Success: ({ result }) => {
      return successResult(
        (result?.newDependencies.length ?? 0) > 0,
        result?.newFiles ?? []
      );
    },
    Error: () => GenerationTaskResultVariant.Error(),
    Loading: () => GenerationTaskResultVariant.Loading(),
    NotRequested: () => GenerationTaskResultVariant.NotRequested(),
  });
};
